import type { HeaderPair } from "../../shared/requestLogRecord";
import { base64ToUint8Array } from "./base64";

export type { HeaderPair };

export type DecodedRequestBody =
  | { kind: "empty" }
  | { kind: "text"; text: string; language?: string }
  | { kind: "binary"; byteLength: number; encoding?: string };

function findHeader(
  headers: readonly HeaderPair[],
  name: string
): string | undefined {
  const lower = name.toLowerCase();
  return headers.find(([key]) => key.toLowerCase() === lower)?.[1];
}

function languageForContentType(
  contentType: string | undefined
): string | undefined {
  const base = contentType?.split(";")[0]?.trim().toLowerCase();
  if (!base) return undefined;
  if (base === "application/json" || base.endsWith("+json")) return "json";
  if (base === "text/html") return "html";
  if (
    base === "application/xml" ||
    base === "text/xml" ||
    base.endsWith("+xml")
  ) {
    return "xml";
  }
  if (base === "text/css") return "css";
  if (base === "application/javascript" || base === "text/javascript") {
    return "javascript";
  }
  return undefined;
}

const DECOMPRESSION_FORMATS: Record<string, CompressionFormat[]> = {
  gzip: ["gzip"],
  "x-gzip": ["gzip"],
  // Some servers send raw deflate despite the header meaning zlib-wrapped.
  deflate: ["deflate", "deflate-raw"],
};

// Bodies are capped at 128 KiB when recorded, so a compressed stream can be
// cut off mid-way; the bytes decompressed before the error are still worth
// showing, matching the "Truncated" badge on the row.
async function decompressSalvaging(
  bytes: Uint8Array,
  format: CompressionFormat
): Promise<Uint8Array | null> {
  const reader = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream(format))
    .getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      total += value.length;
    }
  } catch {
    if (total === 0) return null;
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

async function decompress(
  bytes: Uint8Array,
  encoding: string
): Promise<Uint8Array | null> {
  for (const format of DECOMPRESSION_FORMATS[encoding] ?? []) {
    const out = await decompressSalvaging(bytes, format);
    if (out) return out;
  }
  return null;
}

// Bodies that aren't valid UTF-8 (images, protobuf, etc.) render as a byte
// count instead of mangled text.
function decodeTextBytes(
  bytes: Uint8Array,
  headers: readonly HeaderPair[],
  encoding?: string
): DecodedRequestBody {
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return { kind: "binary", byteLength: bytes.length, encoding };
  }

  const language = languageForContentType(findHeader(headers, "content-type"));
  if (language === "json") {
    try {
      return {
        kind: "text",
        text: JSON.stringify(JSON.parse(text), null, 2),
        language,
      };
    } catch {
      // Not actually valid JSON despite the content-type; fall through to raw text.
    }
  }
  return { kind: "text", text, language };
}

export async function decodeBodyBytes(
  bytes: Uint8Array,
  headers: readonly HeaderPair[] = []
): Promise<DecodedRequestBody> {
  if (bytes.length === 0) return { kind: "empty" };

  const encoding = findHeader(headers, "content-encoding")
    ?.trim()
    .toLowerCase();
  if (encoding && encoding !== "identity") {
    const inflated = await decompress(bytes, encoding);
    if (inflated) {
      return inflated.length === 0
        ? { kind: "empty" }
        : decodeTextBytes(inflated, headers);
    }
    return decodeTextBytes(bytes, headers, encoding);
  }
  return decodeTextBytes(bytes, headers);
}

export async function decodeRequestBody(
  base64: string | undefined,
  headers: readonly HeaderPair[] = []
): Promise<DecodedRequestBody> {
  if (!base64) return { kind: "empty" };
  return decodeBodyBytes(base64ToUint8Array(base64), headers);
}
